// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// battle.ts — one match, decoupled from Trystero, the DOM and the clock (driven by issue() /
// receive() / advance()). That decoupling is what makes the host-transfer and disconnect contracts
// testable with no network at all — see tests/takeover.test.ts.
//
// EVERY MESSAGE CARRIES THE ENTIRE COMMAND LOG, not the latest command. A whole match is a couple of
// hundred commands of four small integers, so the wire cost is nothing, and in exchange a dropped
// datagram becomes unobservable: the next message reconstructs the world. The classic lockstep hang
// — "peer A's order for tick 400 was lost, and now both peers wait for each other for ever" —
// cannot occur, because there is no delta to lose. A 2 s heartbeat re-sends the same log, so a peer
// that goes quiet self-heals within one beat.
//
// NOBODY IS AUTHORITATIVE. The battle is a pure function of (seed, mode, log), so the host holds no
// state anyone else lacks and host transfer is a repaint. What the room DOES need is a way to stop
// when a seat goes away, and that is the WALKOVER: after twelve seconds of silence the survivor is
// declared the winner locally. It is deliberately NOT a simulation event — two peers notice a
// silence at different moments, so a command appended "when the enemy left" would be appended at
// two different ticks and desync the room by construction.

import { botStep, createMind, type BotMind, type Tier } from './bot';
import { canonical, firstDivergence, pack, sameLog, type Command } from './commands';
import { modeOf, type Mode } from './modes';
import {
  CMD_DELAY,
  Cmd,
  RULES_REV,
  advanceTo,
  createState,
  digest,
  step,
  tonnage,
  type SimEvent,
  type State,
} from './sim';
import { UNITS } from './units';
import type { Wire } from './wire';

/** Twelve seconds of nothing from a seat that is meant to be playing. */
const SILENCE_MS = 12_000;
/** How far ahead of the shared clock a peer may run before it waits. */
const TICK_MS = 50;

export type Fault = null | 'build' | 'desync' | 'walkover';

export interface ResultRow {
  seat: number;
  name: string;
  isSelf: boolean;
  isBot: boolean;
  won: boolean;
  tonnage: number;
  built: number;
  lost: number;
  kills: number;
  mined: number;
  salvaged: number;
  /** Ore this side spent that it never got back — the number the design is really about. */
  burnt: number;
}

export interface BattleDeps {
  send: (m: Wire) => void;
  onChange: () => void;
  onEnd: () => void;
  onEvents: (events: SimEvent[], s: State) => void;
}

export interface BattleOpts {
  modeId: string;
  seed: number;
  round: number;
  names: string[];
  /** null = spectator. */
  mySeat: number | null;
  /** Solo only: which seats a local bot plays, and at what strength. */
  bots: Array<Tier | null>;
  deps: BattleDeps;
  networked?: boolean;
}

export class Battle {
  state: State;
  readonly mode: Mode;
  readonly names: string[];
  readonly mySeat: number | null;
  readonly bots: Array<Tier | null>;
  readonly networked: boolean;
  readonly round: number;
  readonly seed: number;
  fault: Fault = null;
  /** Milliseconds since anything was heard from each seat. */
  silence: number[] = [0, 0];
  absent: boolean[] = [false, false];
  log: number[] = [];
  private minds: Array<BotMind | null> = [null, null];
  private handicap: [number, number] = [100, 100];
  private ended = false;
  private accum = 0;
  /**
   * The newest tick we have heard the other peer report.
   *
   * Each peer runs its own clock, so a phone that locks, a tab that is backgrounded, or simply a
   * slower device leaves the two at different points along the same film. That is fine on its own —
   * the battle is a pure function of the log, so they agree about every tick either has run — but a
   * LATE ORDER from the trailing peer forces the leading one to re-derive from the tick it was
   * issued for, and a two-hundred-tick skew makes that a visible jump. Measured in a two-tab test:
   * 639 against 479. So the leader waits rather than sprinting away.
   */
  private peerTick = 0;
  deps: BattleDeps;

  constructor(o: BattleOpts) {
    this.mode = modeOf(o.modeId);
    this.names = o.names.slice();
    this.mySeat = o.mySeat;
    this.bots = o.bots.slice();
    this.deps = o.deps;
    this.round = o.round;
    this.seed = o.seed;
    this.networked = o.networked ?? false;
    for (let seat = 0; seat < 2; seat++) {
      const tier = this.bots[seat];
      if (tier) {
        this.minds[seat] = createMind(seat, tier, o.seed ^ (seat * 0x5bf03635));
        this.handicap[seat] = tier.income;
      }
    }
    this.state = createState(o.seed, this.mode.id, this.handicap);
  }

  get over(): boolean {
    return this.state.over || this.fault !== null;
  }

  /** The tick a command issued right now will execute on, on every peer. */
  get issueTick(): number {
    return this.state.tick + (this.networked ? CMD_DELAY : 0);
  }

  /** Queue an order from the local player. Solo issues at delay 0; a room issues six ticks out. */
  issue(orders: readonly Omit<Command, 'tick' | 'seat'>[]): void {
    if (this.over || this.mySeat === null) return;
    const tick = this.issueTick;
    const add: number[] = [];
    for (const o of orders) add.push(...pack({ tick, seat: this.mySeat, ...o }));
    if (add.length === 0) return;
    this.log = canonical([...this.log, ...add]);
    this.broadcast();
    this.deps.onChange();
  }

  build(type: number): void {
    if (type < 0 || type >= UNITS.length) return;
    this.issue([{ kind: Cmd.Build, unit: -1, arg: type }]);
  }

  broadcast(): void {
    if (!this.networked) return;
    this.deps.send({
      r: this.round,
      l: this.log.slice(),
      t: this.state.tick,
      d: digest(this.state),
      dt: this.state.tick,
      v: RULES_REV,
    });
  }

  noteSeat(seat: number): void {
    if (seat < 0 || seat > 1) return;
    this.silence[seat] = 0;
    if (this.absent[seat]) {
      this.absent[seat] = false;
      this.deps.onChange();
    }
  }

  receive(msg: Wire, fromSeat = -1): void {
    if (!msg || msg.r !== this.round || this.fault !== null) return;
    this.noteSeat(fromSeat);
    if (typeof msg.t === 'number' && msg.t > this.peerTick) this.peerTick = msg.t;
    if (msg.v !== RULES_REV) {
      this.fail('build');
      return;
    }
    const incoming = Array.isArray(msg.l) ? msg.l : [];
    const merged = canonical([...this.log, ...incoming]);
    if (sameLog(merged, this.log)) return;

    // A command that lands for a tick we have ALREADY simulated cannot simply be appended — that
    // would leave the two peers running different histories from the same log, which the digest
    // would report three seconds later as a desync instead of being fixed silently here. Re-derive.
    const from = firstDivergence(this.log, merged);
    this.log = merged;
    if (from >= 0 && from <= this.state.tick) {
      const target = this.state.tick;
      const s = createState(this.seed, this.mode.id, this.handicap);
      const ev: SimEvent[] = [];
      advanceTo(s, target, this.log, ev);
      this.state = s;
      this.deps.onChange();
      this.checkEnd();
      return;
    }
    this.deps.onChange();
  }

  /**
   * Advance the wall clock. Everything time-based lives here and is driven by setInterval, never
   * rAF: a backgrounded tab throttles rAF to nothing, and a match that silently stops advancing is
   * a hang rather than a pause.
   */
  advance(dtMs: number): void {
    if (this.over) return;
    for (let seat = 0; seat < 2; seat++) {
      if (seat !== this.mySeat && this.networked && !this.bots[seat]) this.silence[seat] += dtMs;
    }

    this.accum += dtMs;
    let steps = 0;
    // Hold station if we are more than two seconds ahead of what the other peer has reported. This
    // bounds how far back a late order can ever force a re-derive, and it is a cap rather than a
    // barrier: solo and a room with nobody talking are unaffected, so nothing can deadlock on it.
    const skew = this.networked && this.peerTick > 0 ? this.state.tick - this.peerTick : 0;
    const budget = skew > 40 ? 1 : 20;
    // A full second of catch-up. A backgrounded tab clamps timers to about once a second, and eight
    // steps of catch-up would run the match at a fifth speed for whoever tabbed away.
    while (this.accum >= TICK_MS && steps < budget && !this.over) {
      this.accum -= TICK_MS;
      steps++;
      const ev: SimEvent[] = [];
      // The bot is a local command source at delay 0, feeding the identical log a thumb feeds.
      const out: Command[] = [];
      for (let seat = 0; seat < 2; seat++) {
        const mind = this.minds[seat];
        if (mind && !this.state.over) botStep(this.state, mind, out);
      }
      if (out.length) {
        for (const c of out) this.log.push(...pack(c));
      }
      step(this.state, this.log, ev);
      if (ev.length) this.deps.onEvents(ev, this.state);
    }
    if (this.accum > TICK_MS * 20) this.accum = 0;

    // The walkover. Presentation, never a command: two peers notice a silence at different moments.
    if (this.networked && this.mySeat !== null) {
      const foe = 1 - this.mySeat;
      if (!this.bots[foe] && this.silence[foe] >= SILENCE_MS && !this.state.over) {
        this.absent[foe] = true;
        this.fail('walkover');
        return;
      }
    }
    if (steps > 0) {
      this.deps.onChange();
      this.checkEnd();
    }
  }

  seatLeft(seat: number): void {
    if (seat < 0 || seat > 1) return;
    // Mark it, but let the silence timer be the thing that ends the match: onPeerLeave is not
    // reliable for an abruptly-closed tab, and a peer that merely blinked should not lose.
    this.silence[seat] = Math.max(this.silence[seat], SILENCE_MS - 2000);
    this.deps.onChange();
  }

  /**
   * Host transfer. There is genuinely nothing to take over — the state is derived, not held — so
   * this exists to make that explicit and to re-broadcast, which shortens the window in which a
   * newly promoted peer's neighbours are waiting on a log they already have.
   */
  onHostChanged(): void {
    this.broadcast();
    this.deps.onChange();
  }

  private fail(kind: Exclude<Fault, null>): void {
    if (this.fault !== null) return;
    this.fault = kind;
    // A fault ENDS the match rather than only setting a flag, so nobody is left on a frozen board
    // with a one-line explanation and no way forward.
    this.deps.onChange();
    this.checkEnd();
  }

  private checkEnd(): void {
    if (!this.over || this.ended) return;
    this.ended = true;
    this.deps.onEnd();
  }

  /** Seconds left before the match is adjudicated. */
  get remaining(): number {
    return Math.max(0, Math.ceil((this.mode.hardEnd - this.state.tick) / 20));
  }

  /** Every player's outcome, always — never just the local one. */
  results(): ResultRow[] {
    const s = this.state;
    const winner = this.fault === 'walkover' ? (this.mySeat ?? 0) : s.winner;
    return [0, 1].map((seat) => {
      const side = s.sides[seat];
      return {
        seat,
        name: this.names[seat] ?? `Seat ${seat + 1}`,
        isSelf: this.mySeat === seat,
        isBot: !!this.bots[seat],
        won: winner === seat,
        tonnage: tonnage(s, seat),
        built: side.built,
        lost: side.lost,
        kills: side.kills,
        mined: side.mined,
        salvaged: side.salvaged,
        burnt: Math.max(0, side.spent - side.salvaged),
      };
    });
  }
}
